import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import BookCard from '@/components/ui/BookCard'
import {
  getBooks, useBookCategories,
  type ApiBookCategory, type ApiBookListItem, type BookSort,
} from '@/services/bookService'
import { cn } from '../../app/libs/cn'

// หน้ารายการหนังสือ — ข้อมูลจาก API (jos-cowork-api) เฉพาะหนังสือที่เผยแพร่แล้ว
// ตัวกรองทั้งหมดอยู่ใน URL: ?cat=<category_id>&search=<คำค้น>&sort=<newest|popular|price-asc|price-desc> (แชร์/กดย้อนกลับได้)
const SORT_OPTIONS: { value: BookSort; label: string }[] = [
  { value: 'newest', label: 'ใหม่ล่าสุด' },
  { value: 'popular', label: 'ยอดนิยม' },
  { value: 'price-asc', label: 'ราคาน้อยไปมาก' },
  { value: 'price-desc', label: 'ราคามากไปน้อย' },
]
const DEFAULT_SORT: BookSort = 'newest'
const SEARCH_DEBOUNCE_MS = 350

function CategoryList({ categories, selectedCat, onSelect }: {
  categories: ApiBookCategory[]
  selectedCat: number | null
  onSelect: (id: number | null) => void
}) {
  const total = categories.reduce((sum, c) => sum + c.book_count, 0)
  const item = (active: boolean) =>
    cn('flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
      active ? 'bg-accent/15 font-medium text-accent' : 'text-text-muted hover:bg-surface-2 hover:text-text')
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-text">หมวดหมู่</h3>
      <div className="space-y-1">
        <button onClick={() => onSelect(null)} className={item(selectedCat === null)}>
          <span>ทั้งหมด</span><span className="text-xs opacity-60">{total}</span>
        </button>
        {categories.map((c) => (
          <button key={c.category_id} onClick={() => onSelect(c.category_id)} className={item(selectedCat === c.category_id)}>
            <span>{c.name}</span><span className="text-xs opacity-60">{c.book_count}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function Section({ title, books }: { title: string; books: ApiBookListItem[] }) {
  if (books.length === 0) return null
  return (
    <section className="mb-10">
      <h2 className="mb-5 flex items-center gap-2.5 text-lg font-bold text-text">
        <span className="h-5 w-1 rounded-full bg-accent" />{title}
      </h2>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {books.map((book) => <BookCard key={book.book_id} book={book} />)}
      </div>
    </section>
  )
}

export default function BooksPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const categories = useBookCategories()

  const search = searchParams.get('search') || ''
  const catParam = Number(searchParams.get('cat'))
  const selectedCat = Number.isFinite(catParam) && catParam > 0 ? catParam : null
  const sortParam = searchParams.get('sort') || DEFAULT_SORT
  const sort = (SORT_OPTIONS.some((o) => o.value === sortParam) ? sortParam : DEFAULT_SORT) as BookSort

  // แก้ค่าเดียวใน URL — ค่าว่าง/ค่าเริ่มต้นเอาออกจาก URL ให้ลิงก์สั้น
  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value) next.set(key, value)
      else next.delete(key)
      return next
    }, { replace: true })
  }
  const selectCat = (id: number | null) => setParam('cat', id === null ? null : String(id))
  const setSort = (value: string) => setParam('sort', value === DEFAULT_SORT ? null : value)

  // ช่องค้นหา: พิมพ์ได้ลื่น (state ในเครื่อง) แล้วค่อยอัปเดต URL → ยิง API หลังหยุดพิมพ์ ไม่ยิงทุกตัวอักษร
  const [searchInput, setSearchInput] = useState(search)
  useEffect(() => {
    if (searchInput === search) return
    const t = setTimeout(() => setParam('search', searchInput.trim() || null), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  // ผลลัพธ์เก็บคู่กับเงื่อนไขที่ใช้ค้น — เงื่อนไขเปลี่ยนแล้วผลเดิมไม่ตรง = กำลังโหลด (ไม่ต้อง setState ตอนเริ่มโหลด)
  const queryKey = `${selectedCat ?? ''}|${search}|${sort}`
  const [result, setResult] = useState<{ key: string; books: ApiBookListItem[]; error: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    getBooks({ category_id: selectedCat, search, sort })
      .then((rows) => { if (!cancelled) setResult({ key: queryKey, books: rows, error: '' }) })
      .catch((err) => {
        if (!cancelled) setResult({ key: queryKey, books: [], error: err instanceof Error ? err.message : 'โหลดหนังสือไม่สำเร็จ' })
      })
    return () => { cancelled = true }
  }, [queryKey, selectedCat, search, sort])

  const loading = result?.key !== queryKey
  const books = loading || !result ? [] : result.books
  const error = loading || !result ? '' : result.error

  // ไม่ได้กรอง/ค้นหา/เปลี่ยนการเรียง → แยกกลุ่ม "แนะนำ" กับ "มาใหม่" (ไม่ให้เล่มซ้ำกัน) — ถ้ามีเงื่อนไข แสดงเป็นรายการเดียว
  const isPlainView = selectedCat === null && !search.trim() && sort === DEFAULT_SORT
  const featured = useMemo(() => (isPlainView ? books.filter((b) => b.is_bestseller) : []), [books, isPlainView])
  const latest = useMemo(() => (isPlainView ? books.filter((b) => !b.is_bestseller) : []), [books, isPlainView])
  const totalBooks = categories.reduce((sum, c) => sum + c.book_count, 0)

  return (
    <div className="min-h-screen">
      {/* ส่วนหัว — พื้นขาวเหมือนหน้า /courses (CourseSearchBar) */}
      <div className="border-b border-border bg-surface pb-10 pt-28">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h1 className="font-display text-3xl font-bold text-text md:text-4xl">หนังสือ<span className="text-gradient"> ({totalBooks})</span></h1>
          <p className="mt-2 text-text-muted">หนังสือโลจิสติกส์และการขนส่ง จากประสบการณ์จริง อ่านง่าย นำไปใช้ได้ทันที</p>

          <div className="relative mt-6 max-w-xl">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="ค้นหาชื่อหนังสือหรือชื่อผู้เขียน..."
              className="w-full rounded-2xl border border-border bg-surface-2 py-3.5 pl-11 pr-11 text-sm text-text placeholder-text-muted/60 outline-none transition focus:border-accent/50"
            />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); setParam('search', null) }} aria-label="ล้างคำค้นหา" className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <div className="flex gap-8">
          {/* ตัวกรอง — ซ้ายเหมือนหน้า /courses (มือถือซ่อนไว้ ใช้ปุ่ม "ตัวกรอง") */}
          <aside className="hidden w-60 shrink-0 lg:block">
            <div className="sticky top-24">
              <CategoryList categories={categories} selectedCat={selectedCat} onSelect={selectCat} />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-6 flex items-center justify-between gap-4">
              <p className="text-sm text-text-muted">
                {loading ? 'กำลังโหลด...' : <>พบ <span className="font-semibold text-text">{books.length}</span> เล่ม</>}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs text-text-muted hover:text-text lg:hidden"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" /> ตัวกรอง
                </button>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  aria-label="เรียงตาม"
                  className="rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs text-text focus:outline-none"
                >
                  {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>เรียงตาม: {o.label}</option>)}
                </select>
              </div>
            </div>

            {filtersOpen && (
              <div className="mb-6 rounded-sm border border-border bg-white p-4 lg:hidden">
                <CategoryList categories={categories} selectedCat={selectedCat} onSelect={(id) => { selectCat(id); setFiltersOpen(false) }} />
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <p className="text-sm text-text-muted">กำลังโหลดหนังสือ...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <p className="text-lg font-medium text-text">โหลดหนังสือไม่สำเร็จ</p>
                <p className="mt-1 text-sm text-text-muted">{error}</p>
              </div>
            ) : books.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <p className="text-lg font-medium text-text">{isPlainView ? 'ยังไม่มีหนังสือ' : 'ไม่พบหนังสือที่ค้นหา'}</p>
                {!isPlainView && <p className="mt-1 text-sm text-text-muted">ลองเปลี่ยนคำค้นหาหรือเลือกหมวดหมู่อื่น</p>}
              </div>
            ) : isPlainView ? (
              <>
                <Section title="หนังสือแนะนำ" books={featured} />
                <Section title="หนังสือมาใหม่" books={latest} />
              </>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
                {books.map((book) => <BookCard key={book.book_id} book={book} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
